import { FileItemProps } from '@components/Core/Folders/FileItem/FileItem.component';
import FolderView from '@components/Core/Folders/FolderView/FolderView.component';
import { MergefloServices } from '@Helper/MergefloServices';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addFileToCurrentPath,
  FileTransaction,
  findFileById,
  moveFileToTargetPath,
  removeFileFromCurrentPath,
  replaceFileFromCurrentPath,
} from '@Helper/FolderHelper';
import NewFolderModal from '@/pages/PersonalTemplates/components/NewFolderModal/NewFolderModal.component';
import RemoveFileDialog from '@/pages/PersonalTemplates/components/RemoveFileDialog/RemoveFileDialog.component';
import {
  TemplateLibraryContext,
  TemplateLibraryContextType,
} from '../TemplateLibrary/TemplateLibrary.context';
import MoveFileDialog from '@/pages/PersonalTemplates/components/MoveFileDialog/MoveFileDialog';
import { toast } from 'react-toastify';
import UndoToast from '@components/Popups/UndoToast/UndoToast.component';
import { useDispatch } from 'react-redux';
import { postProjectTemplate } from '@Store/feature/personalTemplatesDetailSlice';

const PersonalTemplatesTab = () => {
  const { t } = useTranslation();
  const rootPathName = t('templates.personal_templates');
  const mergefloService = new MergefloServices();
  const [files, setFiles] = useState<FileItemProps[]>([]);
  const [isNewModalOpen, setNewModalOpen] = useState<boolean>(false);
  const [openRemoveFileDialog, removeFileDialog] = useState<boolean>(false);
  const [targetFile, setTargetFile] = useState<FileItemProps | null>(null);
  const [moveFileDialog, setMoveFileDialog] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const dispatch = useDispatch();
  const { handleFileSelected } = useContext(
    TemplateLibraryContext
  ) as TemplateLibraryContextType;

  useEffect(() => {
    mergefloService.getPersonalTemplates().then((files: FileItemProps[]) => {
      setFiles(files);
    });
  }, []);

  const createNewFolder = () => {
    setNewModalOpen(true);
  };

  const handleCreateNewFolder = (file: FileItemProps) => {
    setNewModalOpen(false);
    const functionName = targetFile
      ? replaceFileFromCurrentPath
      : addFileToCurrentPath;
    const newFiles = functionName(
      {
        name: rootPathName,
        type: 'folder',
        files,
      } as FileItemProps,
      file,
      targetFile ? getFilePaths(targetFile) : currentPath,
      targetFile ?? ({} as FileItemProps)
    );
    setFiles([...newFiles.files]);
    mergefloService
      .setTemplateStructure({ personal_templates: newFiles.files })
      .then(_ => {
        setTargetFile(null);
        mergefloService.getTemplateStructure().then((template: any) => {
          console.log({ template });
          dispatch(
            postProjectTemplate(
              (template['personal_templates'] as FileItemProps[]) || []
            )
          );
        });
      });
  };

  const onActionMenuItemClick = (file: FileItemProps, action: string) => {
    const fileWithPath = findFileById(file, files);
    setTargetFile(fileWithPath);
    const actions = {
      delete: () => {
        removeFileDialog(true);
      },
      edit: () => {
        setNewModalOpen(true);
      },
      move: () => {
        setMoveFileDialog(true);
      },
      rename: () => {
        setNewModalOpen(true);
      },
      default: () => {
        console.log(file, action);
      },
    };
    actions[action as keyof typeof actions]();
  };

  const handleRemoveFile = (file: FileItemProps) => {
    removeFileDialog(false);
    const fileWithPath = findFileById(file, files);
    const newFiles = removeFileFromCurrentPath(
      {
        name: rootPathName,
        files,
      } as FileItemProps,
      fileWithPath,
      getFilePaths(fileWithPath)
    );
    setFiles([...newFiles.files]);
    setTargetFile(null);
    mergefloService.setTemplateStructure({
      personal_templates: newFiles.files,
    });
  };

  const getFilePaths = (file: FileItemProps): string[] => {
    const { path = '' } = file;
    return path.split('/').filter(item => item.length > 0);
  };

  const runFileTransaction = (paths: string[], targetPaths: string[]) => {
    if (targetFile) {
      const transaction: FileTransaction = {
        sourcePath: {
          name: rootPathName,
          files,
        } as FileItemProps,
        file: targetFile,
        paths,
        targetPaths,
      };
      const newFiles = moveFileToTargetPath(transaction);
      setFiles([...newFiles.files]);
      mergefloService.setTemplateStructure({
        personal_templates: newFiles.files,
      });
    }
  };

  const onMoveFileToTargetPath = (targetPaths: string[]) => {
    if (targetFile) {
      setMoveFileDialog(false);
      runFileTransaction([rootPathName, ...currentPath], targetPaths);
      toast(
        <UndoToast
          text={`${targetFile.name} move to ${targetPaths.reverse()[0]}`}
          onUndo={() => {
            runFileTransaction(targetPaths, [rootPathName, ...currentPath]);
          }}
        />,
        {
          onClose: () => {
            setTargetFile(null);
          },
        }
      );
    }
  };

  const handlePathChange = (paths: string[]) => {
    setCurrentPath(paths);
  };

  return (
    <div style={{ height: '262px', overflowY: 'auto' }}>
      <FolderView
        files={files as FileItemProps[]}
        homePath={t('navigation.home_folder')}
        onActionMenuItemClick={onActionMenuItemClick}
        handlePathChange={handlePathChange}
        handleFileSelected={handleFileSelected}
      />
      <MoveFileDialog
        open={moveFileDialog}
        files={files}
        handleClose={() => setMoveFileDialog(false)}
        handleSubmit={onMoveFileToTargetPath}
      />
      <NewFolderModal
        open={isNewModalOpen}
        file={targetFile || undefined}
        handleClose={() => setNewModalOpen(false)}
        handleSubmit={handleCreateNewFolder}
      />
      {targetFile && (
        <RemoveFileDialog
          open={openRemoveFileDialog}
          file={targetFile}
          handleClose={() => removeFileDialog(false)}
          handleSubmit={handleRemoveFile}
        />
      )}
    </div>
  );
};

export default PersonalTemplatesTab;
